/**
 * Fila de vendas que a rede não deixou enviar.
 *
 * O caso que ela cobre é a queda CURTA: o Wi-Fi do balcão pisca, a operadora
 * oscila, o servidor reinicia. Antes disso a venda simplesmente falhava — o
 * vendedor via um toast de erro com o cliente na frente, e a saída era esperar
 * olhando para a tela ou refazer tudo depois.
 *
 * O que NÃO é: um modo de vender sem servidor. A venda enfileirada só existe de
 * verdade quando o servidor a grava, e é lá que ela ganha número, baixa estoque,
 * entra no caixa e vira NFC-e — tudo com a data do reenvio. `origin`,
 * `offline_created_at` e `device_id` viajam junto só como AUDITORIA: é o que
 * responde depois "esta venda ficou presa em qual balcão, e por quanto tempo".
 *
 * A segurança contra duplicar é a idempotência que já existe: o item guarda o
 * `client_uuid` gerado quando o vendedor fechou a venda e repete SEMPRE o mesmo.
 * Se a venda tiver sido gravada e só a resposta ter se perdido, o reenvio recebe
 * a venda antiga de volta em vez de criar outra.
 */

import { uuidV4 } from "./clientUuid";

const CHAVE_FILA = "osp_pos_fila_offline";
const CHAVE_DEVICE = "osp_device_id";

/* Teto de sanidade. Fila com dezenas de vendas não é mais "a rede piscou": é o
   servidor fora do ar há muito tempo, e continuar aceitando venda que ninguém
   sabe se vai entrar é pior que parar e avisar. */
export const MAX_FILA = 30;

/**
 * Quantas rodadas sem rede uma venda aguenta antes de pedir atenção.
 *
 * A rodada roda a cada 20s e para na PRIMEIRA falha de rede, então isto são
 * cerca de 20 minutos tentando. O teto não existe para desistir da venda — ela
 * continua guardada e o vendedor pode mandar de novo com um clique. Existe para
 * a venda parar de tentar em SILÊNCIO.
 *
 * O caso que ensinou isso: um 500 do servidor sem cabeçalho de CORS é
 * indistinguível de queda de rede para o navegador — ele esconde a resposta e o
 * axios reporta erro de rede. A venda entrava na fila e ficava reenviando para
 * sempre, a cada 20 segundos, sem nada na tela. (A causa foi corrigida no
 * servidor; este teto é a rede de segurança para a próxima da mesma família.)
 */
export const MAX_TENTATIVAS = 60;

/**
 * Identidade DESTE computador, estável entre vendas e entre sessões.
 *
 * Vai no `device_id` da venda. É o que permite responder, depois, de qual balcão
 * veio a venda que ficou presa — sem isso a auditoria de origem não distingue um
 * terminal do outro. Fica em localStorage (e não em sessionStorage) de propósito:
 * a máquina continua a mesma depois de fechar o navegador.
 */
export function deviceId() {
  try {
    const salvo = localStorage.getItem(CHAVE_DEVICE);
    if (salvo) return salvo;
    const novo = `pdv-${uuidV4()}`;   // 40 caracteres: cabe no limite de 64 da API
    localStorage.setItem(CHAVE_DEVICE, novo);

    return novo;
  } catch {
    return `pdv-${uuidV4()}`;   // modo privado: sem persistência, mas ainda identifica a sessão
  }
}

/**
 * Só falha de REDE entra na fila.
 *
 * Se o servidor respondeu — 422 de estoque, 409 de identidade reaproveitada, 403
 * de permissão —, ele decidiu, e a transação não passou. Enfileirar isso ficaria
 * repetindo para sempre um pedido que nunca vai ser aceito, e escondendo do
 * vendedor um erro que ele resolve na hora.
 */
export function ehFalhaDeRede(error) {
  if (!error) return false;
  if (error.response) return false;   // houve resposta: é decisão do servidor
  return Boolean(error.request) || error.code === "ERR_NETWORK" || error.code === "ECONNABORTED";
}

function lerBruto() {
  try {
    const cru = localStorage.getItem(CHAVE_FILA);
    const dados = cru ? JSON.parse(cru) : [];

    return Array.isArray(dados) ? dados : [];
  } catch {
    return [];   // storage indisponível ou conteúdo corrompido: fila vazia
  }
}

function gravar(itens) {
  try {
    localStorage.setItem(CHAVE_FILA, JSON.stringify(itens));
  } catch { /* sem persistência: a fila vale só para esta aba */ }

  return itens;
}

/** Vendas na fila, da mais antiga para a mais nova (é a ordem de envio). */
export function lerFila() {
  return lerBruto();
}

export function contarPendentes(fila) {
  return (fila || []).filter((i) => i.status === "pendente").length;
}

export function contarFalhas(fila) {
  return (fila || []).filter((i) => i.status === "falhou").length;
}

/**
 * Guarda a venda que não conseguiu subir.
 *
 * `payload` é o corpo exato do POST, com o `client_uuid` que o PDV já tinha
 * gerado. `receipt` é o cupom montado na hora do fechamento: sem ele, a venda
 * entraria depois sem nada para imprimir, porque o carrinho já foi limpo.
 */
export function enfileirar({ payload, receipt }) {
  const fila = lerBruto();
  if (fila.length >= MAX_FILA) {
    const err = new Error(`A fila de vendas em espera chegou ao limite de ${MAX_FILA}. Restabeleça a conexão antes de registrar mais vendas.`);
    err.code = "fila_cheia";
    throw err;
  }

  const item = {
    client_uuid: payload.client_uuid,
    // A marcação de origem é feita AQUI, e não no envio: o que interessa à
    // auditoria é quando o vendedor fechou a venda no balcão, não quando a rede
    // voltou. A venda em si nasce no servidor com a data do reenvio.
    payload: {
      ...payload,
      origin: "offline",
      offline_created_at: new Date().toISOString(),
      device_id: deviceId(),
    },
    receipt,
    criado_em: new Date().toISOString(),
    status: "pendente",
    tentativas: 0,
    erro: null,
  };
  gravar([...fila, item]);

  return item;
}

function atualizar(clientUuid, mudanca) {
  return gravar(lerBruto().map((i) => (i.client_uuid === clientUuid ? { ...i, ...mudanca } : i)));
}

export function removerDaFila(clientUuid) {
  return gravar(lerBruto().filter((i) => i.client_uuid !== clientUuid));
}

/** Volta a tentar um item que o servidor recusou (depois de o caixa ser aberto, por exemplo). */
export function reenfileirar(clientUuid) {
  return atualizar(clientUuid, { status: "pendente", erro: null });
}

/* Evita duas rodadas simultâneas: o gatilho de "voltou a rede" e o do intervalo
   disparam juntos com frequência, e enviar o mesmo item duas vezes ao mesmo
   tempo só faria o servidor resolver a corrida à toa. */
let enviando = false;

/**
 * Tenta enviar tudo que está pendente, um de cada vez.
 *
 * `enviar(payload)` é quem fala com a API — a fila não conhece axios, o que a
 * deixa testável sem rede. Recusa do servidor marca o item como `falhou` e PARA
 * de tentar: é erro que precisa de gente (estoque acabou, caixa fechado). Falha
 * de rede deixa o item pendente para a próxima rodada.
 */
export async function enviarPendentes(enviar) {
  if (enviando) return { enviadas: [], falhas: [], ocupado: true };
  enviando = true;
  const enviadas = [];
  const falhas = [];

  try {
    for (const item of lerBruto()) {
      if (item.status !== "pendente") continue;
      try {
        const venda = await enviar(item.payload);
        removerDaFila(item.client_uuid);
        enviadas.push({ item, venda });
      } catch (e) {
        if (ehFalhaDeRede(e)) {
          // Ainda sem rede: para a rodada inteira. Insistir nos próximos só
          // gastaria tempo esperando o mesmo timeout várias vezes.
          const tentativas = (item.tentativas || 0) + 1;
          if (tentativas >= MAX_TENTATIVAS) {
            const erro = `Sem resposta do servidor depois de ${tentativas} tentativas. A venda continua guardada aqui.`;
            atualizar(item.client_uuid, { status: "falhou", erro, tentativas });
            falhas.push({ item, erro });
          } else {
            atualizar(item.client_uuid, { tentativas });
          }
          break;
        }
        const motivo = e?.response?.data?.detail || e?.message || "Recusada pelo servidor";
        atualizar(item.client_uuid, { status: "falhou", erro: String(motivo), tentativas: (item.tentativas || 0) + 1 });
        falhas.push({ item, erro: String(motivo) });
      }
    }
  } finally {
    enviando = false;
  }

  return { enviadas, falhas, ocupado: false };
}
